// @ts-strict-ignore
import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import mergeWith from 'lodash/mergeWith';

import ccStyles from 'iron/common/query-builder/chart-customization/axis/index.module.styl';
import {ButtonReact} from 'common/components/button';
import {ButtonLayout, ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {useTranslation} from 'common/i18n/hooks';
import {InputReact} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {ToggleReact} from 'common/components/toggle';
import {DecimalOption} from 'common/query-builder/chart-customization/axis';
import {formatMetricNumberWithUnits} from 'common/util';
import {DropMenuReact} from 'common/components/drop-menu';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {BEHAVIOR_METRIC_ABBREVIATION_DEFAULT, BehaviorMetricDisplay} from 'common/report/insights/util/types';
import {getMetricFormattingDefaultsFromQueryEntry} from 'common/report/insights/charts/util';
import {EntryWithDisplay} from 'common/query-builder/query-entry-section/types';

import styles from './index.module.styl';

const CLEAR_VALUE: null = null;
const PREVIEW_VALUE = 1245532.36;
const DEFAULT_UNIT = `#`;
const CUSTOM_UNIT = `Custom`;
const PREFIX_PRESETS = [`$`];
const SUFFIX_PRESETS = [`%`];
const ALL_PRESETS = [...PREFIX_PRESETS, ...SUFFIX_PRESETS, DEFAULT_UNIT, CUSTOM_UNIT];

const getButtonValue = (prefix?: string, suffix?: string) => {
  return prefix?.[0] || suffix?.[0] || DEFAULT_UNIT;
};

const getToggleValue = (prefix?: string, suffix?: string) => {
  if (!prefix && !suffix) {
    return DEFAULT_UNIT;
  } else if (prefix && suffix) {
    return CUSTOM_UNIT;
  } else if (ALL_PRESETS.includes(prefix)) {
    return prefix;
  } else if (ALL_PRESETS.includes(suffix)) {
    return suffix;
  }
  return CUSTOM_UNIT;
};

const DecimalRow = ({
  display,
  defaults,
  entry,
  setEntry,
}: {
  display: BehaviorMetricDisplay;
  defaults?: BehaviorMetricDisplay;
  entry: EntryWithDisplay;
  setEntry: (entry: EntryWithDisplay) => void;
}) => {
  const {t} = useTranslation();

  const precision = display.precision ?? defaults?.precision ?? CLEAR_VALUE;
  const setPrecision = (newPrecision: number) => {
    const newClause = {...entry};
    newClause.display = {...(entry.display || {}), precision: newPrecision};
    setEntry(newClause);
  };
  return (
    <div className={clsx(ccStyles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(ccStyles.ccSublabel)}>{t(`chart-customization.decimals`)}</label>
      </div>
      <div style={{gridColumn: 2}}>
        <DecimalOption precision={precision} setPrecision={setPrecision} isLeft />
      </div>
      <div style={{gridColumn: 3}}>
        <DecimalOption precision={precision} setPrecision={setPrecision} isLeft={false} />
      </div>
    </div>
  );
};

const AbbrevRow = ({
  display,
  entry,
  setEntry,
}: {
  display: BehaviorMetricDisplay;
  entry: EntryWithDisplay;
  setEntry: (clause: EntryWithDisplay) => void;
}) => {
  const {t} = useTranslation();
  return (
    <div className={clsx(ccStyles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(ccStyles.ccSublabel)}>{t(`chart-customization.abbreviation`)}</label>
      </div>
      <div className={clsx(ccStyles.singleInputColumn)}>
        <ToggleReact
          className={clsx(ccStyles.singleInput)}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: t(`common.yes`), value: `true`},
            {label: t(`common.no`), value: `false`},
          ]}
          selected={String(display.abbrev ?? BEHAVIOR_METRIC_ABBREVIATION_DEFAULT)}
          onSelect={(ev) => {
            const newEntry = {...entry};
            newEntry.display = {...(entry.display || {}), abbrev: ev.detail.selected === `true`};
            setEntry(newEntry);
          }}
        />
      </div>
    </div>
  );
};

const PreviewRow = ({display}: {display: BehaviorMetricDisplay}) => {
  return (
    <div className={clsx(ccStyles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(ccStyles.ccSublabel)}>Preview</label>
      </div>
      <div style={{gridColumn: `2 / span 2`}}>{formatMetricNumberWithUnits(PREVIEW_VALUE, display)}</div>
    </div>
  );
};

const MetricUnitRow = ({
  display,
  defaults,
  entry,
  setEntry,
}: {
  display: BehaviorMetricDisplay;
  defaults?: BehaviorMetricDisplay;
  entry: EntryWithDisplay;
  setEntry: (clause: EntryWithDisplay) => void;
}) => {
  const {t} = useTranslation();

  return (
    <div className={clsx(ccStyles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(ccStyles.ccSublabel)}>{t(`chart-customization.axis-unit`)}</label>
      </div>
      <div style={{gridColumn: 2}}>
        <InputReact
          ariaLabel={t(`chart-customization.prefix`)}
          className={clsx(ccStyles.twoInputColumn)}
          size={InputSize.Medium}
          placeholder={defaults?.prefix ?? t(`chart-customization.prefix`)}
          value={display.prefix || ``}
          onChange={(ev) => {
            const newEntry = {...entry};
            newEntry.display = {...(entry.display || {}), prefix: ev.detail.value || CLEAR_VALUE};
            setEntry(newEntry);
          }}
        />
      </div>
      <div style={{gridColumn: 3}}>
        <InputReact
          ariaLabel={t(`chart-customization.suffix`)}
          className={clsx(ccStyles.twoInputColumn)}
          size={InputSize.Medium}
          placeholder={defaults?.suffix ?? t(`chart-customization.suffix`)}
          value={entry.display?.suffix || ``}
          onChange={(ev) => {
            const newEntry = {...entry};
            newEntry.display = {...(entry.display || {}), suffix: ev.detail.value || CLEAR_VALUE};
            setEntry(newEntry);
          }}
        />
      </div>
    </div>
  );
};

export function MetricUnitsButton(props: {
  entry: EntryWithDisplay;
  propertyDisplay?: BehaviorMetricDisplay;
  insideFooter?: boolean;
  updateOpen: () => void;
  size?: ButtonSize;
}) {
  const defaultsFromEntry = getMetricFormattingDefaultsFromQueryEntry(props.entry);
  const inheritedDefaults = {
    ...defaultsFromEntry,
    ...props.propertyDisplay,
  };

  const mergedOptions: BehaviorMetricDisplay = {
    ...inheritedDefaults,
    ...props.entry.display,
  };

  return (
    <div className={clsx(styles.metricUnitsButton)}>
      <ButtonReact
        className={clsx(styles.unitSelectionButton)}
        theme={ButtonTheme.Secondary}
        size={props.size || ButtonSize.Small}
        style={{marginRight: `8px`}}
        layout={ButtonLayout.Horizontal}
        onClick={() => props.updateOpen()}
      >
        <span style={{marginLeft: props.insideFooter ? `-3px` : undefined}}>
          {getButtonValue(mergedOptions.prefix, mergedOptions.suffix)}
        </span>
      </ButtonReact>
    </div>
  );
}

export function MetricUnitsSelector(props: {
  entry: EntryWithDisplay;
  setEntry: (entry: EntryWithDisplay) => void;
  propertyDisplay?: BehaviorMetricDisplay;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const inheritedDefaults = {
    ...getMetricFormattingDefaultsFromQueryEntry(props.entry),
    // Default metric units should be overridden by units assigned to a property
    // in Lexicon.
    ...props.propertyDisplay,
  };
  const mergedOptions: BehaviorMetricDisplay = {
    ...props.entry.display,
  };
  // We don't want the default null/undefined values from the entry itself to
  // override the non-empty inherited values.
  mergeWith(mergedOptions, {abbrev: true, precision: undefined, ...inheritedDefaults}, (objectValue, sourceValue) => {
    const finalValue = objectValue ?? sourceValue;
    return finalValue;
  });

  const [preset, setPreset] = useState<string>();

  useEffect(() => {
    setPreset((x) => {
      if (x === CUSTOM_UNIT) {
        return x;
      }
      return getToggleValue(mergedOptions.prefix, mergedOptions.suffix);
    });
  }, [mergedOptions.prefix, mergedOptions.suffix]);

  return (
    <DropMenuReact
      style={{width: `100%`}}
      open={props.open}
      offset={12}
      positionStrategy={PositioningStrategy.Absolute}
      direction={Directions.Down}
      forceStacking
      onChange={(ev) => {
        props.setOpen(ev.detail.state === `open`);
      }}
    >
      <div className={clsx(styles.metricSettingsBlock)}>
        <ToggleReact
          className={clsx(ccStyles.singleInput)}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={ALL_PRESETS.map((unit) => ({label: unit, value: unit}))}
          selected={preset}
          onSelect={(ev) => {
            const selected = ev.detail.selected;
            setPreset(selected);

            const newEntry = {...props.entry};
            if (PREFIX_PRESETS.includes(selected)) {
              newEntry.display = {
                ...(props.entry.display || {}),
                prefix: ev.detail.selected,
                suffix: CLEAR_VALUE,
              };
            } else if (SUFFIX_PRESETS.includes(selected)) {
              newEntry.display = {
                ...(props.entry.display || {}),
                prefix: CLEAR_VALUE,
                suffix: ev.detail.selected,
              };
            } else if (selected === DEFAULT_UNIT || selected === CUSTOM_UNIT) {
              newEntry.display = {
                ...(props.entry.display || {}),
                prefix: CLEAR_VALUE,
                suffix: CLEAR_VALUE,
              };
            }
            props.setEntry(newEntry);
          }}
        />
        {preset === CUSTOM_UNIT && (
          <MetricUnitRow
            display={mergedOptions}
            defaults={inheritedDefaults}
            entry={props.entry}
            setEntry={props.setEntry}
          />
        )}
        <AbbrevRow display={mergedOptions} entry={props.entry} setEntry={props.setEntry} />
        <DecimalRow
          display={mergedOptions}
          defaults={inheritedDefaults}
          entry={props.entry}
          setEntry={props.setEntry}
        />
        <PreviewRow display={mergedOptions} />
      </div>
    </DropMenuReact>
  );
}
